import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { QRScannerModal } from '../components/QRScannerModal';

const Home = () => {
    const navigate = useNavigate();
    const { config } = useConfig();
    const { user } = useAuth();
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);

    // Default premium menu buttons
    const primaryColor = config?.primaryColor || "#0a58ca";
    const secondaryColor = config?.secondaryColor || "#ffa800";

    const defaultButtons = [
        {
            label: "ESCANEAR PUNTOS QR",
            action: 'qr_modal',
            color: secondaryColor
        },
        {
            label: "MAPA CIRCUITO F1",
            path: "/circuito",
            color: primaryColor
        },
        {
            label: "PODIO DE GANADORES",
            path: "/podio",
            color: primaryColor
        },
        {
            label: "AGENDA",
            path: "/agenda",
            color: primaryColor
        },
        {
            label: "SPEAKERS",
            path: "/speakers",
            color: secondaryColor
        },
        {
            label: "PREGUNTAS AL EXPOSITOR",
            path: "/questions-expert",
            color: primaryColor
        },
        {
            label: "CARTA LOGÍSTICA",
            path: "/welcome",
            color: secondaryColor,
            isExternal: true
        }
    ];

    // Use configurable buttons if defined in configs, otherwise fallback to dynamically styled defaults
    const menuButtons = config?.home_buttons || defaultButtons;

    // Filter buttons based on whether their corresponding module is enabled and user role permits
    const activeButtons = menuButtons.filter(btn => {
        if (btn.path && btn.path.includes('podio')) {
            const isStaffOrAdmin = user && ['staff', 'admin'].includes(user.role);
            const isPodiumEnabledForUser = config?.pages?.podium === true;
            if (!isStaffOrAdmin && !isPodiumEnabledForUser) return false;
        }

        let key = null;
        if (btn.path && btn.path.includes('agenda')) key = 'agenda';
        else if (btn.path && btn.path.includes('speakers')) key = 'speakers';
        else if (btn.path && btn.path.includes('questions-expert')) key = 'questions_private';
        else if (btn.path && btn.path.includes('welcome')) key = 'welcome';

        if (key) {
            const design = config?.pageDesigns?.[key];
            if (design && design.enabled === false) return false;
        }
        return true;
    });

    // Sort buttons alphabetically by label
    const sortedButtons = [...activeButtons].sort((a, b) => 
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
    );

    const handleButtonClick = (btn) => {
        if (btn.action === 'qr_modal') {
            setIsQrModalOpen(true);
        } else if (btn.action === 'guide_modal') {
            setIsGuideModalOpen(true);
        } else if (btn.isExternal || (btn.path && btn.path.startsWith('http'))) {
            window.open(btn.path, '_blank', 'noopener,noreferrer');
        } else if (btn.path) {
            navigate(btn.path);
        }
    };

    return (
        <div className="flex flex-col items-center justify-start min-h-[75vh] w-full relative z-10 select-none animate-fadeIn">
            {/* Quick Guide Feature Button at the top */}
            <div className="w-full max-w-sm px-6 mt-4">
                <button
                    onClick={() => setIsGuideModalOpen(true)}
                    className="w-full bg-[#07241c] hover:bg-[#0b382c] border-2 border-[#00e676]/60 text-[#00e676] py-3 px-5 rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
                >
                    <span className="text-base">📖</span>
                    <span>¿Cómo participar y acumular puntos?</span>
                </button>
            </div>

            {/* Main Navigation Menu Buttons Container */}
            <div className="w-full max-w-sm px-6 flex flex-col gap-5 items-center mt-6 mb-8">
                {sortedButtons.map((btn, idx) => (
                    <button
                        key={idx}
                        onClick={() => handleButtonClick(btn)}
                        style={{ backgroundColor: primaryColor }}
                        className="w-full text-white py-4 px-6 rounded-lg font-black uppercase tracking-widest text-[14px] sm:text-[16px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center flex items-center justify-center cursor-pointer"
                    >
                        {btn.label}
                    </button>
                ))}
            </div>

            {/* QR Scanner Modal */}
            <QRScannerModal
                isOpen={isQrModalOpen}
                onClose={() => setIsQrModalOpen(false)}
            />

            {/* HOME PARTICIPANT GUIDE MODAL */}
            {isGuideModalOpen && createPortal(
                <div className="fixed inset-0 z-[99999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-5 shadow-2xl text-left relative max-h-[90vh] overflow-y-auto custom-scrollbar">
                        <button
                            onClick={() => setIsGuideModalOpen(false)}
                            className="absolute top-5 right-5 text-white/60 hover:text-white text-xl font-bold"
                        >
                            ✕
                        </button>

                        <div className="flex items-center gap-3 border-b border-[#00e676]/30 pb-3">
                            <span className="text-3xl">🏁</span>
                            <div>
                                <h3 className="text-lg font-black text-[#00e676] uppercase tracking-wider">
                                    Guía del Participante
                                </h3>
                                <p className="text-[10px] text-slate-300 font-bold uppercase tracking-wider">
                                    Experiencia Fórmula 1 Boehringer Ingelheim
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4 text-xs text-slate-200">
                            {/* Step 1: QR Scanning */}
                            <div className="bg-[#07241c] p-4 rounded-2xl border border-[#00e676]/30 space-y-2">
                                <div className="flex items-center gap-2 font-black text-[#00e676] uppercase tracking-wider text-xs">
                                    <span>📱</span> 1. Escanear Puntos QR en Estaciones
                                </div>
                                <p className="text-slate-300 text-[11px] leading-relaxed">
                                    Presiona el botón <strong>"ESCANEAR PUNTOS QR"</strong> y escanea los códigos QR ubicados en cada estación física del evento para registrar tu asistencia y sumar puntos a tu perfil.
                                </p>
                                <div className="bg-[#0b382c] border border-[#00e676]/40 p-2.5 rounded-xl text-[10px] text-[#00e676] font-bold flex items-center gap-2">
                                    <span>💡</span>
                                    <span>Una vez escaneado el código QR, regresa al inicio de la aplicación para continuar navegando o ingresar a otras actividades.</span>
                                </div>
                            </div>

                            {/* Step 2: Circuit & Games */}
                            <div className="bg-[#07241c] p-4 rounded-2xl border border-[#00e676]/30 space-y-2">
                                <div className="flex items-center gap-2 font-black text-[#00e676] uppercase tracking-wider text-xs">
                                    <span>🏎️</span> 2. Circuito, Desafíos y Trivias
                                </div>
                                <p className="text-slate-300 text-[11px] leading-relaxed">
                                    Consulta el <strong>Mapa del Circuito</strong> para ubicar las estaciones y participa en los juegos de destreza (<strong>Laberinto F1</strong> y <strong>Concéntrese</strong>) para acumular la mayor cantidad de puntos en el menor tiempo posible.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsGuideModalOpen(false)}
                            className="w-full bg-[#00e676] text-[#07241c] py-3.5 px-6 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:scale-105 active:scale-95 transition-all text-center cursor-pointer mt-2"
                        >
                            ¡ENTENDIDO Y LISTO PARA COMPETIR!
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Home;

